import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ExternalLink, Network, PencilLine } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { commerceSecondaryLine } from '../../../../../utils/commerceSearch';
import { formatCurrency } from '../../utils/cobroHelpers';
import { holdingFichaPath, missingFieldsText } from '../../utils/holdings';
import HoldingFiscalForm from './HoldingFiscalForm';
import HoldingCollectionProfileForm from './HoldingCollectionProfileForm';

// Rótulo de un bloqueo que no necesita datos de la fila para explicarse (claves i18n).
const BLOCKER_TEXTS = {
  membership_conflict:
    'Hay empresas asociadas a más de un holding: no se puede saber a quién cobrarles. Resolvelo desde la ficha del holding antes de emitir.',
  missing_pricing:
    'El holding no tiene tarifa propia. No se cobra con la tarifa base del operador: cargala en la ficha del holding.',
  consolidated_disabled:
    'El cobro consolidado del holding está apagado: sus empresas se cobran sueltas. Si corresponde, encendelo desde la ficha del holding.',
  // La nota superaría el máximo de líneas de billing (un documento por nota). No se resuelve en
  // la ficha: hay que partir el período.
  too_many_lines:
    'No se puede emitir: supera el máximo de líneas por nota; emití por un período más corto.',
};

// Bloqueos que se resuelven en la ficha del holding (no en el paso "Datos del holding").
const FICHA_BLOCKERS = ['missing_pricing', 'membership_conflict', 'consolidated_disabled'];

/**
 * Bloque de UN holding en la vista previa del asistente de cobro (`perHolding[i]` de billing):
 * sus cifras, el detalle por empresa y, si no se puede emitir, por qué y cómo destrabarlo.
 *
 * Los datos que faltan (giro, dirección, contacto, plazo) se completan acá mismo, en el paso
 * "Datos del holding", con los mismos formularios de la ficha. La tarifa y los conflictos de
 * membresía no: se resuelven en la ficha, que se abre en otra pestaña para no perder la tanda.
 *
 * Guardar NO recalcula solo: el operador recalcula cuando terminó con los dos formularios. El
 * asistente mantiene la tarjeta montada mientras recalcula (lo escrito y sin guardar no se
 * pierde) y se entera de cada guardado (`onFiscalSaved`, `onProfileSaved`) para no dejar
 * confirmar una vista previa vieja.
 *
 * @param {{ holding: object, fiscalHolding?: object|null, chargingCollected: boolean,
 *           companyNameOf: (companyId: string) => string|null,
 *           onFiscalSaved: (view: object) => void, onProfileSaved?: (view: object) => void,
 *           onRecalculate: () => void, recalculating?: boolean }} props
 *   `holding` es el elemento de `perHolding`; `fiscalHolding`, el de GET contracts/holdings
 *   (para precargar los datos fiscales; null si no se pudo leer). `onFiscalSaved` recibe la
 *   vista del PATCH fiscal-profile y `onProfileSaved` la del PUT del perfil de cobro. Mientras
 *   `recalculating`, el botón de recalcular espera (la tarjeta sigue montada, con sus datos).
 */
export default function HoldingPreviewCard({
  holding: h,
  fiscalHolding,
  chargingCollected,
  companyNameOf,
  onFiscalSaved,
  onProfileSaved,
  onRecalculate,
  recalculating = false,
}) {
  const { t } = useTranslation();
  const detailId = useId();
  const [detailOpen, setDetailOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);

  const cur = h.currency;
  const name = h.name || fiscalHolding?.name || t('Sin nombre');
  const secondary = commerceSecondaryLine({
    name,
    legalName: h.legalName,
    businessIdentifier: h.businessIdentifier,
  });
  const blockers = Array.isArray(h.blockers) ? h.blockers : [];
  const companies = Array.isArray(h.companies) ? h.companies : [];
  const conflictNames = (h.conflicts ?? []).map(
    (c) => companyNameOf(c.companyId) || c.companyId,
  );
  const fichaLink = (
    <a
      href={holdingFichaPath(h.holdingId)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-brand-600 hover:underline"
    >
      {t('Abrir la ficha del holding')}
      <ExternalLink className="w-3.5 h-3.5" aria-hidden />
    </a>
  );

  return (
    <section
      className="bill-card"
      style={{ marginBottom: '1rem', padding: '0.875rem 1rem' }}
      aria-label={t('Holding {{name}}', { name })}
    >
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <span className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="d-flex align-items-center gap-2 flex-wrap">
            <Network className="w-4 h-4 text-fg-muted" aria-hidden />
            <span className="bill-td-strong">{name}</span>
            <span className="bill-badge bill-badge--info bill-badge--sm">
              {t('Holding · {{n}} empresas', { n: h.memberCount ?? companies.length })}
            </span>
            {h.blocked && (
              <span className="bill-badge bill-badge--error bill-badge--sm">{t('No se emite')}</span>
            )}
            {h.taxMode === 'inclusive' && (
              <span className="bill-badge bill-badge--info bill-badge--sm">{t('IVA incl.')}</span>
            )}
            {h.taxMode === 'exclusive' && (
              <span className="bill-badge bill-badge--info bill-badge--sm">{t('+ IVA')}</span>
            )}
          </span>
          {secondary && <span className="bill-hint d-block">{secondary}</span>}
        </span>
        <span className="text-right">
          <span className="bill-td-amount d-block" style={{ fontSize: '1rem' }}>
            {formatCurrency(h.totalAmount, cur)}
          </span>
          <span className="bill-hint d-block">{t('{{n}} paquetes', { n: h.packageCount ?? 0 })}</span>
        </span>
      </div>

      {/* Qué se va a cobrar y con qué plazo: el mismo desglose que un comercio. */}
      <div style={{ marginTop: '0.5rem' }}>
        {!h.blocked && (
          <span className="bill-hint d-block">
            {h.paymentTerm
              ? h.paymentTerm.source === 'holding'
                ? t('Vence a los {{n}} días (plazo del holding).', { n: h.paymentTerm.days })
                : t('Vence a los {{n}} días (plazo general del operador).', { n: h.paymentTerm.days })
              : t('Sin vencimiento: ni el holding ni el operador tienen plazo.')}
          </span>
        )}
        {h.unpricedCount > 0 && (
          <span className="bill-hint d-block">{t('{{n}} sin tarifar', { n: h.unpricedCount })}</span>
        )}
        {h.oversized?.count > 0 && (
          <span className="bill-hint d-block">
            {h.oversized.surchargeAmount > 0
              ? t('{{n}} sobredimensionados (+{{amount}})', {
                  n: h.oversized.count,
                  amount: formatCurrency(h.oversized.surchargeAmount, cur),
                })
              : t('{{n}} sobredimensionados (sin recargo)', { n: h.oversized.count })}
          </span>
        )}
        {h.returned?.packagesCount > 0 && (
          <span className="bill-hint d-block">
            {t('{{n}} devueltos (−{{amount}})', {
              n: h.returned.packagesCount,
              amount: formatCurrency(h.returned.amount, cur),
            })}
          </span>
        )}
        {h.codCompensation?.amount > 0 && (
          <span className="bill-hint d-block">
            {t('Compensa {{amount}} de pago contra entrega de sus empresas.', {
              amount: formatCurrency(h.codCompensation.amount, cur),
            })}
          </span>
        )}
        {h.modeWarnings?.billedInOtherMode > 0 && (
          <span className="bill-hint d-block">
            {chargingCollected
              ? t('{{n}} ya cobrados por entrega (quedan fuera)', { n: h.modeWarnings.billedInOtherMode })
              : t('{{n}} ya cobrados por recolección (quedan fuera)', {
                  n: h.modeWarnings.billedInOtherMode,
                })}
          </span>
        )}
        {h.modeWarnings?.unbilledBeforePeriod > 0 && (
          <span className="bill-hint d-block text-status-warning">
            {t('{{n}} sin cobrar con fecha anterior al período', {
              n: h.modeWarnings.unbilledBeforePeriod,
            })}
          </span>
        )}
        {/* El volumen de la escala suma los pedidos de TODAS sus empresas. */}
        {h.volumeScale?.tier && (
          <span className="bill-hint d-block">
            {t('{{volume}} en el ciclo entre todas sus empresas → tramo desde {{minQty}}: {{price}} c/u', {
              volume: h.volumeScale.volume,
              minQty: h.volumeScale.tier.minQty,
              price: formatCurrency(h.volumeScale.tier.unitPrice, cur),
            })}
          </span>
        )}
      </div>

      {h.blocked && (
        <div
          className="bill-recap"
          role="alert"
          style={{ background: 'rgb(var(--status-error-soft))', marginTop: '0.75rem', marginBottom: 0 }}
        >
          <div
            className="bill-recap__title"
            style={{ color: 'rgb(var(--status-error))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
          >
            <AlertTriangle className="w-4 h-4" aria-hidden />
            {t('La nota de este holding no se puede emitir')}
          </div>
          <ul className="list-unstyled mb-2">
            {blockers.map((blocker) => (
              <li key={blocker} className="bill-hint" style={{ marginBottom: '0.25rem' }}>
                {blocker === 'missing_profile_fields'
                  ? t('Faltan datos del holding: {{fields}}.', {
                      fields: missingFieldsText(h.missingProfileFields, t),
                    })
                  : blocker === 'cycle_mismatch'
                    ? t('No se puede emitir: se factura por ciclo {{cycle}} ({{from}} a {{to}})', {
                        cycle: h.volumeScale?.scale === 'weekly' ? t('semanal') : t('mensual'),
                        from: h.volumeScale?.expectedCycle?.start,
                        to: h.volumeScale?.expectedCycle?.end,
                      })
                    : t(BLOCKER_TEXTS[blocker] ?? 'No se puede emitir ({{reason}}).', { reason: blocker })}
                {blocker === 'membership_conflict' && conflictNames.length > 0 && (
                  <span className="d-block">
                    {t('Empresas en conflicto: {{names}}', { names: conflictNames.join(', ') })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="d-flex gap-3 align-items-center flex-wrap">
            {blockers.includes('missing_profile_fields') && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<PencilLine className="w-4 h-4" />}
                aria-expanded={dataOpen}
                onClick={() => setDataOpen((open) => !open)}
              >
                {dataOpen ? t('Ocultar los datos del holding') : t('Completar los datos del holding')}
              </Button>
            )}
            {blockers.some((blocker) => FICHA_BLOCKERS.includes(blocker)) && fichaLink}
          </div>
        </div>
      )}

      {/* Paso "Datos del holding": los mismos formularios de la ficha, sin salir de la tanda. */}
      {dataOpen && (
        <div className="bill-card" style={{ marginTop: '0.75rem', padding: '1rem' }}>
          <h4 className="bill-card__title" style={{ marginBottom: '0.75rem' }}>
            {t('Datos del holding')}
          </h4>
          <h5 className="bill-label">{t('Datos fiscales')}</h5>
          {fiscalHolding ? (
            <HoldingFiscalForm key={fiscalHolding.id} holding={fiscalHolding} onSaved={onFiscalSaved} />
          ) : (
            <p className="bill-hint">
              {t('No se pudieron cargar los datos fiscales del holding: completalos desde su ficha.')}{' '}
              {fichaLink}
            </p>
          )}
          <h5 className="bill-label" style={{ marginTop: '1.25rem' }}>
            {t('Contacto y plazo')}
          </h5>
          <HoldingCollectionProfileForm holdingId={h.holdingId} onSaved={onProfileSaved} />
          <div
            className="d-flex justify-content-between align-items-center gap-2 flex-wrap"
            style={{ marginTop: '1rem' }}
          >
            <span className="bill-hint">
              {t('Cuando termines de guardar, recalculá para ver si la nota del holding ya se puede emitir.')}
            </span>
            <Button size="sm" onClick={onRecalculate} disabled={recalculating}>
              {t('Recalcular la vista previa')}
            </Button>
          </div>
        </div>
      )}

      {companies.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
            aria-expanded={detailOpen}
            aria-controls={detailOpen ? detailId : undefined}
            onClick={() => setDetailOpen((open) => !open)}
          >
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${detailOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
            {t('Detalle por empresa ({{n}})', { n: companies.length })}
          </button>
          {detailOpen && (
            <div
              id={detailId}
              className="bill-grid"
              role="table"
              aria-label={t('Detalle por empresa')}
              style={{ '--bill-cols': '1.6fr 0.7fr 1fr 1fr', marginTop: '0.5rem' }}
            >
              <div className="bill-grid__head" role="row">
                <span className="bill-grid__hcell" role="columnheader">{t('Empresa')}</span>
                <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Paquetes')}</span>
                <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Subtotal')}</span>
                <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Pago contra entrega')}</span>
              </div>
              {companies.map((c) => (
                <div className="bill-grid__row" role="row" key={c.companyId}>
                  <span role="cell">{c.companyName || companyNameOf(c.companyId) || c.companyId}</span>
                  <span className="bill-grid__cell--end bill-td-muted" role="cell">{c.packageCount}</span>
                  <span className="bill-grid__cell--end bill-td-amount" role="cell">
                    {formatCurrency(c.subtotal, cur)}
                  </span>
                  <span className="bill-grid__cell--end bill-td-muted" role="cell">
                    {c.codCount > 0 ? `−${formatCurrency(c.codAmount, cur)}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
